// src/App.js — Route definitions
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
import { PeekProvider } from './components/Peek'
import FirmCompletionHost from './components/FirmCompletionHost'
import { PortalPeekProvider } from './components/portal/PortalPeek'
import { PortalContactPage, PortalFirmPage } from './pages/portal/PortalRecordPage'
import Login        from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import SetPassword  from './pages/SetPassword'
import Dashboard    from './pages/Dashboard'
import Mandates     from './pages/MandatePipeline'
import MandateDetail from './pages/MandateDetail'
import Contacts     from './pages/Contacts'
import Partners     from './pages/Partners'
import PersonProfile from './pages/PersonProfile'
import Firms        from './pages/Firms'
import Interactions from './pages/Interactions'
import Settings     from './pages/Settings'
import Query        from './pages/Query'
import Events       from './pages/Events'
import EventDetail  from './pages/EventDetail'
import Ventures     from './pages/Ventures'
import VentureDetail from './pages/VentureDetail'
import PortalLayout       from './components/portal/PortalLayout'
import PortalMandates     from './pages/portal/PortalMandates'
import PortalDashboard    from './pages/portal/PortalDashboard'
import PortalSubmitDeal   from './pages/portal/PortalSubmitDeal'
import PortalMandateDetail from './pages/portal/PortalMandateDetail'
import PortalAddNames     from './pages/portal/PortalAddNames'
import PortalExpressInterest from './pages/portal/PortalExpressInterest'
import PortalContacts     from './pages/portal/PortalContacts'
import PortalSettings     from './pages/portal/PortalSettings'

function NotFound() {
  return (
    <div className="page">
      <div className="empty-state" style={{ paddingTop: '80px' }}>
        <h3>Page not found</h3>
        <p>The page you're looking for doesn't exist.</p>
      </div>
    </div>
  )
}

// Old /contacts and /partners links and bookmarks: same page under its new
// name, keeping the query string so ?open=<id> still opens that person.
function Renamed({ to }) {
  const { search } = useLocation()
  const { id } = useParams()
  return <Navigate to={(id ? `${to}/${id}` : to) + search} replace />
}

function ProtectedApp() {
  return (
    <Layout>
      <PeekProvider>
      <Routes>
        <Route path="/"               element={<Dashboard />} />
        <Route path="/query"          element={<Query />} />
        <Route path="/mandates"       element={<Mandates />} />
        <Route path="/mandates/:id"   element={<MandateDetail />} />
        <Route path="/individuals"    element={<Contacts />} />
        <Route path="/individuals/:id" element={<PersonProfile />} />
        <Route path="/rga-partners"   element={<Partners />} />
        <Route path="/rga-partners/:id" element={<PersonProfile />} />
        <Route path="/contacts"       element={<Renamed to="/individuals" />} />
        <Route path="/contacts/:id"   element={<Renamed to="/individuals" />} />
        <Route path="/partners"       element={<Renamed to="/rga-partners" />} />
        <Route path="/partners/:id"   element={<Renamed to="/rga-partners" />} />
        <Route path="/firms"          element={<Firms />} />
        <Route path="/interactions"   element={<Interactions />} />
        <Route path="/events"         element={<Events />} />
        <Route path="/events/:id"     element={<EventDetail />} />
        <Route path="/ventures"       element={<Ventures />} />
        <Route path="/ventures/:id"   element={<VentureDetail />} />
        <Route path="/settings"       element={<Settings />} />
        <Route path="*"               element={<NotFound />} />
      </Routes>
      </PeekProvider>
      <FirmCompletionHost />
    </Layout>
  )
}

function PortalApp() {
  return (
    <PortalLayout>
      <PortalPeekProvider>
      <Routes>
        <Route path="/"                        element={<PortalDashboard />} />
        <Route path="/mandates"                element={<PortalMandates />} />
        <Route path="/submit-deal"             element={<PortalSubmitDeal />} />
        <Route path="/mandates/:id"            element={<PortalMandateDetail />} />
        <Route path="/mandates/:id/add-names"  element={<PortalAddNames />} />
        <Route path="/mandates/:id/interest"   element={<PortalExpressInterest />} />
        <Route path="/contacts"                element={<PortalContacts />} />
        <Route path="/contacts/:id"            element={<PortalContactPage />} />
        <Route path="/firms/:id"               element={<PortalFirmPage />} />
        <Route path="/settings"                element={<PortalSettings />} />
        <Route path="*"                        element={<NotFound />} />
      </Routes>
      </PortalPeekProvider>
    </PortalLayout>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login"           element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/set-password"    element={<SetPassword />} />
        {/* One login for everyone; role decides where you land (ProtectedRoute).
            This just catches old bookmarks/links to the former partner-only URL. */}
        <Route path="/portal/login" element={<Navigate to="/login" replace />} />
        <Route
          path="/portal/*"
          element={
            <ProtectedRoute partnerOnly>
              <PortalApp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <ProtectedApp />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  )
}
